import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'

import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { cn } from '#/shared/lib/utils'
import {
  CheckIcon,
  EmailIcon,
  ExternalLinkIcon,
  PhoneIcon,
  PinIcon,
  UserOutlineIcon,
} from '#/shared/ui/icons'
import { CallableEmail } from '#/shared/ui/email-address'
import { CallablePhone } from '#/shared/ui/phone-number'

import { buildSidebarCarrier } from '../lib/build-sidebar-carrier'
import type {
  SidebarCarrierInfo,
  SidebarCarrierInsurance,
  SidebarCarrierInsuranceStatus,
} from '../lib/build-sidebar-carrier'
import { useCarrierInfo } from '../model/use-carrier-info'
import { SidebarSection } from './sidebar-section'

type Carrier = NonNullable<ShipmentDetail['carrier']>

export function SidebarCarrierSection({
  shipment,
}: {
  shipment: ShipmentDetail
}) {
  const carrier = shipment.carrier
  if (!carrier) return null
  return <CarrierSectionInner brief={carrier} />
}

function CarrierSectionInner({ brief }: { brief: Carrier }) {
  const { t } = useTranslation()
  const { carrier } = useCarrierInfo(brief.id)
  const info = buildSidebarCarrier(brief, carrier)

  return (
    <SidebarSection label={t('detailSidebar.carrier.title')} inset>
      <div className="flex flex-col gap-2.5">
        <CarrierIdentity info={info} />
        <CarrierContact info={info} />
        <CarrierInsurance insurance={info.insurance} />
      </div>
    </SidebarSection>
  )
}

interface IdentifierItem {
  label: string
  emphasized?: boolean
}

function buildIdentifierItems(
  info: SidebarCarrierInfo,
  t: TFunction,
): IdentifierItem[] {
  const items: (IdentifierItem | undefined)[] = [
    info.mc
      ? {
          label: t('detailSidebar.carrier.mc', { number: info.mc }),
          emphasized: true,
        }
      : undefined,
    info.dot
      ? {
          label: t('detailSidebar.carrier.dot', { number: info.dot }),
          emphasized: true,
        }
      : undefined,
    info.trucks
      ? { label: t('detailSidebar.carrier.trucks', { count: info.trucks }) }
      : undefined,
    info.drivers
      ? { label: t('detailSidebar.carrier.drivers', { count: info.drivers }) }
      : undefined,
  ]
  return items.filter((item): item is IdentifierItem => Boolean(item))
}

function CarrierIdentity({ info }: { info: SidebarCarrierInfo }) {
  const { t } = useTranslation()
  const identifiers = buildIdentifierItems(info, t)
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-base font-semibold text-gray-900">
          {info.name}
        </span>
        {info.isAuthorized && <AuthorizedPill />}
      </div>
      {identifiers.length > 0 && <IdentifierRow items={identifiers} />}
      {info.address && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <PinIcon className="size-3.5 shrink-0" />
          <span className="min-w-0 truncate">{info.address}</span>
        </div>
      )}
    </div>
  )
}

function AuthorizedPill() {
  const { t } = useTranslation()
  return (
    <span className="inline-flex h-4.5 shrink-0 items-center gap-1.5 rounded-full bg-green-50 px-1.75 text-2xs font-medium whitespace-nowrap text-green-700">
      <CheckIcon className="size-2.5 shrink-0" />
      {t('detailSidebar.carrier.authorized')}
    </span>
  )
}

function IdentifierRow({ items }: { items: IdentifierItem[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-1.25 text-xs text-gray-500">
      {items.map((item, index) => (
        <Fragment key={item.label}>
          {index > 0 && (
            <span aria-hidden className="text-gray-300">
              ·
            </span>
          )}
          <span className={cn(item.emphasized && 'font-medium')}>
            {item.label}
          </span>
        </Fragment>
      ))}
    </div>
  )
}

function CarrierContact({ info }: { info: SidebarCarrierInfo }) {
  if (!info.contactName && !info.phone && !info.email) return null
  return (
    <>
      <Divider />
      <div className="flex flex-col gap-1.5">
        {info.contactName && (
          <div className="flex items-center gap-1.5">
            <UserOutlineIcon className="size-4 shrink-0 text-gray-500" />
            <span className="truncate text-sm font-medium text-gray-900">
              {info.contactName}
            </span>
          </div>
        )}
        {info.phone && <PhoneRow phone={info.phone} />}
        {info.email && <EmailRow email={info.email} />}
      </div>
    </>
  )
}

function PhoneRow({ phone }: { phone: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <PhoneIcon className="size-3.5 shrink-0 text-gray-500" />
      <CallablePhone
        value={phone}
        className="text-xs font-medium text-gray-500 underline"
      />
    </div>
  )
}

function EmailRow({ email }: { email: string }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <EmailIcon className="size-3.5 shrink-0 text-gray-500" />
      <CallableEmail
        value={email}
        className="text-xs font-medium text-gray-500 underline"
        target={{ email }}
        hideIcon
      />
    </div>
  )
}

function CarrierInsurance({
  insurance,
}: {
  insurance?: SidebarCarrierInsurance
}) {
  const { t } = useTranslation()
  if (!insurance) return null
  return (
    <>
      <Divider />
      <div className="flex flex-col gap-1.25">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-gray-500 uppercase">
            {t('detailSidebar.carrier.insurance')}
          </span>
          {insurance.status && (
            <InsuranceStatusPill status={insurance.status} />
          )}
          {insurance.fileUrl && (
            <a
              href={insurance.fileUrl}
              target="_blank"
              rel="noreferrer"
              className="flex shrink-0 items-center gap-1.25 text-2xs-plus text-primary hover:underline"
            >
              {t('detailSidebar.carrier.openInsurance')}
              <ExternalLinkIcon className="size-4 shrink-0" />
            </a>
          )}
        </div>
        <div className="flex flex-wrap items-start gap-3.5">
          {insurance.expirationDate && (
            <InsuranceStat label={t('detailSidebar.carrier.expDate')}>
              {insurance.expirationDate}
            </InsuranceStat>
          )}
          {insurance.autoLiability && (
            <InsuranceStat label={t('detailSidebar.carrier.autoLiability')}>
              {insurance.autoLiability}
            </InsuranceStat>
          )}
          {insurance.cargo && (
            <InsuranceStat label={t('detailSidebar.carrier.cargo')}>
              {insurance.cargo}
              {insurance.cargoDeductible && (
                <span className="text-xs font-normal">
                  {' · '}
                  {t('detailSidebar.carrier.deductible', {
                    amount: insurance.cargoDeductible,
                  })}
                </span>
              )}
            </InsuranceStat>
          )}
        </div>
      </div>
    </>
  )
}

function InsuranceStatusPill({
  status,
}: {
  status: SidebarCarrierInsuranceStatus
}) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'inline-flex h-4 shrink-0 items-center gap-1.5 rounded-full px-1.5 text-2xs font-medium whitespace-nowrap',
        status.isActive
          ? 'bg-green-50 text-green-700'
          : 'bg-red-50 text-red-500',
      )}
    >
      <span aria-hidden className="size-1.25 rounded-full bg-current" />
      {status.isActive
        ? t('detailSidebar.carrier.insuranceActive', { days: status.daysLeft })
        : t('detailSidebar.carrier.insuranceExpired')}
    </span>
  )
}

function InsuranceStat({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-2xs text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{children}</span>
    </div>
  )
}

function Divider() {
  return <div className="-mx-2.5 h-px bg-gray-200" />
}
